import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  BookOpen, 
  Plus, 
  Search, 
  Filter, 
  Users, 
  Calendar,
  Edit,
  Trash2,
  Eye,
  MoreVertical,
  TrendingUp,
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Tag,
  X,
  ChevronDown,
  Save
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trainingsService, Training } from '../../services/trainings.service';
import ApprovalStatusBadge from '../../components/common/ApprovalStatusBadge';
import { formatTrainingLocation } from '../../utils/address';
// import ApprovalNotification from '../../components/common/ApprovalNotification';
import { API_ENDPOINTS } from '../../config/api';

// Use the utility function for location display
const getLocationDisplay = (training: Training): string => {
  return formatTrainingLocation(training);
};

const TrainingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedFormat, setSelectedFormat] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    totalRevenue: 0,
    averageRating: 0
  });
  const [notification, setNotification] = useState<{
    type: 'approved' | 'rejected' | 'pending';
    trainingTitle: string;
    rejectionReason?: string;
    isVisible: boolean;
  }>({
    type: 'pending',
    trainingTitle: '',
    isVisible: false
  });
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedTrainingId, setSelectedTrainingId] = useState<string | null>(null);
  const [sessionDate, setSessionDate] = useState('');
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  // Domain options from the CreateTrainingPage
  const domainOptions = [
    { id: 1, name: 'Affaires Réglementaires Générales' },
    { id: 2, name: 'Bonnes Pratiques de Fabrication (BPF)' },
    { id: 3, name: 'Bonnes Pratiques Cliniques (BPC)' },
    { id: 4, name: 'Bonnes Pratiques de Laboratoire (BPL)' },
    { id: 5, name: 'Pharmacovigilance' },
    { id: 6, name: 'Audit Qualité et Conformité' },
    { id: 7, name: 'Systèmes de Management de la Qualité (SMQ)' },
    { id: 8, name: 'Validation et Qualification' },
    { id: 9, name: 'Développement Pharmaceutique' },
    { id: 10, name: 'Recherche et Développement (R&D)' },
    { id: 11, name: 'Formulation et Galénique' },
    { id: 12, name: 'Chimie Analytique Pharmaceutique' },
    { id: 13, name: 'Biotechnologie Pharmaceutique' },
    { id: 14, name: 'Production et Fabrication' },
    { id: 15, name: 'Opérations de Fabrication Stérile' },
    { id: 16, name: 'Contrôle Qualité de Production' },
    { id: 17, name: 'Ingénierie et Maintenance Pharmaceutique' },
    { id: 18, name: 'Chaîne d\'Approvisionnement Pharmaceutique' },
    { id: 19, name: 'Technologies de Fabrication Avancées' },
    { id: 20, name: 'Affaires Commerciales et Marketing' },
    { id: 21, name: 'Marketing Pharmaceutique' },
    { id: 22, name: 'Vente Pharmaceutique' },
    { id: 23, name: 'Accès au Marché' },
    { id: 24, name: 'Économie de la Santé' },
    { id: 25, name: 'Management et Leadership' },
    { id: 26, name: 'Gestion de Projet Pharmaceutique' },
    { id: 27, name: 'Leadership et Gestion d\'Équipe' },
    { id: 28, name: 'Stratégie et Innovation' },
    { id: 29, name: 'Sécurité et Environnement' },
    { id: 30, name: 'Santé, Sécurité, Environnement (SSE)' },
    { id: 31, name: 'Biosécurité' },
    { id: 32, name: 'Autres Domaines Spécialisés' },
    { id: 33, name: 'Dispositifs Médicaux' },
    { id: 34, name: 'Cosmétiques' },
    { id: 35, name: 'Nutrition et Diététique' }
  ];

  const formatOptions = [
    { value: 'En présentiel', label: 'En présentiel' },
    { value: 'En ligne', label: 'En ligne' },
    { value: 'Hybride', label: 'Hybride' }
  ];

  useEffect(() => {
    fetchTrainings();
    fetchStats();
  }, []);

  const fetchTrainings = async () => {
    try {
      setLoading(true);
      const data = await trainingsService.getAllTrainings();
      setTrainings(data);
      setError(null);
      
      // Debug: Log all unique format values
      const uniqueFormats = [...new Set(data.map(training => training.format).filter(Boolean))];
      
      // Debug: Log image data and constructed URLs
      
      // Test a specific image URL
      if (data.length > 0 && data[0].banner_image) {
        const testUrl = `${API_ENDPOINTS.UPLOADS}/${data[0].banner_image}`;
        
        // Test if the image loads
        const img = new Image();
        img.onload = () => {};
        img.onerror = (e) => {};
        img.src = testUrl;
      }
    } catch (err) {
      setError('Erreur lors du chargement des formations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await trainingsService.getTrainingStats();
      setStats({
        total: data.total,
        active: data.active,
        completed: data.completed,
        totalRevenue: 0, // This would need to be calculated from the backend
        averageRating: 0 // This would need to be calculated from the backend
      });
    } catch (err) {
    }
  };

  const handleDeleteTraining = async (id: string) => {
    if (window.confirm(t('trainingCenter.trainings.messages.confirmDelete'))) {
      try {
        await trainingsService.deleteTraining(id);
        await fetchTrainings(); // Refresh the list
        alert(t('trainingCenter.trainings.messages.deleteSuccess'));
      } catch (err) {
        alert(t('trainingCenter.trainings.messages.deleteError'));
      }
    }
  };

  const handleAddSession = (trainingId: string) => {
    setSelectedTrainingId(trainingId);
    setSessionDate('');
    setShowSessionModal(true);
  };

  const handleCreateSession = async () => {
    if (!selectedTrainingId || !sessionDate) {
      alert(t('trainingCenter.trainings.messages.sessionDateRequired'));
      return;
    }

    setIsCreatingSession(true);
    try {
      // Create session via API
      await trainingsService.createSession(selectedTrainingId, { session_date: sessionDate });
      setShowSessionModal(false);
      setSelectedTrainingId(null);
      setSessionDate('');
      alert(t('trainingCenter.trainings.messages.sessionCreated'));
      // Optionally refresh the trainings list
      await fetchTrainings();
    } catch (error) {
      alert(t('trainingCenter.trainings.messages.sessionError'));
    } finally {
      setIsCreatingSession(false);
    }
  };

  const handleCloseSessionModal = () => {
    setShowSessionModal(false);
    setSelectedTrainingId(null);
    setSessionDate('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'En cours':
        return 'text-brand-secondary bg-brand-secondary/10';
      case 'Terminé':
        return 'text-brand-primary bg-brand-primary/10';
      case 'Planifié':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getApprovalStatus = (training: Training) => {
    if (training.isApproved === true) {
      return {
        status: 'Approuvé',
        color: 'text-green-600 bg-green-50',
        icon: CheckCircle,
        iconColor: 'text-green-600'
      };
    } else if (training.isApproved === false) {
      return {
        status: 'Rejeté',
        color: 'text-red-600 bg-red-50',
        icon: XCircle,
        iconColor: 'text-red-600'
      };
    } else {
      return {
        status: 'En attente de validation',
        color: 'text-yellow-600 bg-yellow-50',
        icon: AlertCircle,
        iconColor: 'text-yellow-600'
      };
    }
  };

  const getStatusFromDate = (scheduleDate: string) => {
    if (!scheduleDate) return 'Planifié';
    const today = new Date();
    const trainingDate = new Date(scheduleDate);
    if (isNaN(trainingDate.getTime())) return 'Planifié';

    if (trainingDate < today) {
      return 'Terminé';
    } else if (trainingDate.getTime() - today.getTime() < 7 * 24 * 60 * 60 * 1000) {
      return 'En cours';
    } else {
      return 'Planifié';
    }
  };

  const filteredTrainings = trainings.filter(training => {
    const matchesSearch = training.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || getStatusFromDate(training.schedule_date) === selectedStatus;
    const matchesDomain = selectedDomain === 'all' || training.domaine_id === parseInt(selectedDomain);
    const matchesFormat = selectedFormat === 'all' || training.format === selectedFormat;
    
    // Debug logging for format filter
    if (selectedFormat !== 'all' && training.format) {
    }
    
    return matchesSearch && matchesStatus && matchesDomain && matchesFormat;
  });

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedStatus('all');
    setSelectedDomain('all');
    setSelectedFormat('all');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (selectedDomain !== 'all') count++;
    if (selectedFormat !== 'all') count++;
    return count;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={fetchTrainings}
          className="bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-brand-primary/90"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-surface">
      <div className="space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-primary to-brand-secondary rounded-2xl p-8 text-white">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-3">
                {t('trainingCenter.trainings.title')}
              </h1>
              <p className="text-white/90 text-lg">
                {t('trainingCenter.trainings.subtitle')}
              </p>
            </div>
            <button 
              onClick={async () => {
                try {
                  const { canCreate, message } = await trainingsService.canCreateTraining();
                  if (canCreate) {
                    navigate('/training-center/trainings/create');
                  } else {
                    const go = window.confirm(`${message || "Vous devez avoir un abonnement actif pour créer une nouvelle formation."}\n\nSouhaitez-vous consulter les plans d'abonnement ?`);
                    if (go) navigate('/training-center/subscriptions');
                  }
                } catch (e) {
                  navigate('/training-center/trainings/create');
                }
              }}
              className="mt-6 lg:mt-0 bg-white text-brand-primary px-8 py-4 rounded-xl font-semibold hover:bg-white/90 transition-all duration-200 flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Plus className="h-6 w-6" />
              {t('trainingCenter.trainings.newTraining')}
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4">
          <div className="flex flex-col gap-4">
            {/* Search Bar and Quick Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('trainingCenter.trainings.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 text-sm"
                  />
                </div>
              </div>

              {/* Quick Filter Buttons - Responsive */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedStatus(selectedStatus === 'active' ? 'all' : 'active')}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                    selectedStatus === 'active' 
                      ? 'bg-brand-primary text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('trainingCenter.trainings.filters.active')}
                </button>
                <button
                  onClick={() => setSelectedStatus(selectedStatus === 'completed' ? 'all' : 'completed')}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                    selectedStatus === 'completed' 
                      ? 'bg-brand-secondary text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('trainingCenter.trainings.filters.completed')}
                </button>
                <button
                  onClick={() => setSelectedStatus(selectedStatus === 'pending' ? 'all' : 'pending')}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                    selectedStatus === 'pending' 
                      ? 'bg-brand-mint text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('trainingCenter.trainings.filters.pending')}
                </button>
              </div>

              {/* Advanced Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center justify-center gap-2 px-3 py-2.5 border border-gray-200 rounded-lg hover:border-brand-primary hover:bg-brand-primary/5 transition-all duration-200 min-w-[120px]"
              >
                <Filter className="h-4 w-4" />
                <span className="text-sm font-medium hidden sm:inline">{t('common.more')}</span>
                <span className="text-sm font-medium sm:hidden">{t('common.filters')}</span>
                {getActiveFiltersCount() > 0 && (
                  <span className="bg-brand-primary text-white text-xs rounded-full px-1.5 py-0.5 min-w-[16px] text-center">
                    {getActiveFiltersCount()}
                  </span>
                )}
                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="border-t border-gray-100 pt-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Domain Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Domaine
                    </label>
                    <select
                      value={selectedDomain}
                      onChange={(e) => setSelectedDomain(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 text-sm"
                    >
                      <option value="all">Tous les domaines</option>
                      {domainOptions.map((domain) => (
                        <option key={domain.id} value={domain.id}>
                          {domain.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Format Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Format
                    </label>
                    <select
                      value={selectedFormat}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 text-sm"
                    >
                      <option value="all">Tous les formats</option>
                      {formatOptions.map((format) => (
                        <option key={format.value} value={format.value}>
                          {format.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Clear Filters */}
                {getActiveFiltersCount() > 0 && (
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={clearAllFilters}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:text-brand-primary transition-colors duration-200"
                    >
                      <X className="h-3 w-3" />
                      Effacer tous les filtres
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">Total Formations</p>
                <p className="text-xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-primary/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">Formations Actives</p>
                <p className="text-xl font-bold text-gray-900">{stats.active}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-brand-secondary to-brand-secondary/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Users className="h-5 w-5 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">Formations Terminées</p>
                <p className="text-xl font-bold text-gray-900">{stats.completed}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-brand-mint to-brand-mint/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-md border border-gray-100 p-4 hover:shadow-lg transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">Revenus Totaux</p>
                <p className="text-lg font-bold text-gray-900">{stats.totalRevenue.toLocaleString()} DZD</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-brand-accent to-brand-accent/80 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Trainings Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredTrainings.map((training, index) => {
            const status = getStatusFromDate(training.schedule_date);
            return (
              <motion.div
                key={training.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 group"
              >
                {/* Image */}
                <div className="h-40 bg-gray-200 relative overflow-hidden">
                  {training.banner_image ? (
                    <img
                      src={`${API_ENDPOINTS.UPLOADS}/${training.banner_image}`}
                      alt={training.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.currentTarget.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10">
                      <BookOpen className="h-10 w-10 text-brand-primary" />
                    </div>
                  )}
                  
                  {/* Status Badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)} border border-white/20 backdrop-blur-sm`}>
                      {status}
                    </span>
                  </div>

                  {/* Approval Status Badge */}
                  <div className="absolute top-2 right-2">
                    <ApprovalStatusBadge 
                      status={training.status || (training.isApproved === true ? 'approved' : training.isApproved === false ? 'refused' : 'pending')}
                      size="sm"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-brand-primary transition-colors duration-200 line-clamp-2">
                      {training.title}
                    </h3>
                    
                    {/* Domain */}
                    {training.domaine && (
                      <div className="flex items-center gap-1 mb-2">
                        <Tag className="h-3 w-3 text-brand-secondary" />
                        <span className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded-full">
                          {training.domaine.name}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Rejection Reason */}
                  {training.isApproved === false && training.rejectionReason && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs text-red-800">
                        <strong>Motif de refus :</strong> {training.rejectionReason}
                      </p>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="text-center p-2 bg-gradient-to-br from-brand-primary/5 to-brand-primary/10 rounded-lg border border-brand-primary/20">
                      <div className="text-sm font-bold text-brand-primary">{training.pricing_dzd || 0}</div>
                      <div className="text-xs text-gray-600">Prix (DZD)</div>
                    </div>
                    <div className="text-center p-2 bg-gradient-to-br from-brand-secondary/5 to-brand-secondary/10 rounded-lg border border-brand-secondary/20">
                      <div className="text-sm font-bold text-brand-secondary">{training.format || 'N/A'}</div>
                      <div className="text-xs text-gray-600">Format</div>
                    </div>
                  </div>

                  {/* Location and Date */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <div className="w-5 h-5 bg-brand-mint/20 rounded flex items-center justify-center">
                        <Calendar className="h-3 w-3 text-brand-mint" />
                      </div>
                      <span className="truncate">{getLocationDisplay(training)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <div className="w-5 h-5 bg-brand-accent/20 rounded flex items-center justify-center">
                        <Clock className="h-3 w-3 text-brand-accent" />
                      </div>
                      <span>
                        {training.schedule_date && !isNaN(new Date(training.schedule_date).getTime())
                          ? new Date(training.schedule_date).toLocaleDateString('fr-FR')
                          : t('trainingCenter.trainings.messages.noSession')}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button 
                      onClick={() => navigate(`/training-center/trainings/${training.id}`)}
                      className="flex-1 bg-gradient-to-r from-brand-primary to-brand-primary/90 text-white py-2 px-3 rounded-lg font-medium hover:from-brand-primary/90 hover:to-brand-primary/80 transition-all duration-200 text-sm"
                    >
                      Gérer
                    </button>
                    <button 
                      onClick={() => handleAddSession(training.id)}
                      className="flex-1 bg-gradient-to-r from-brand-secondary to-brand-secondary/90 text-white py-2 px-3 rounded-lg font-medium hover:from-brand-secondary/90 hover:to-brand-secondary/80 transition-all duration-200 text-sm"
                    >
                      Ajouter Session
                    </button>
                    <div className="flex gap-1">
                      <button 
                        onClick={() => navigate(`/training-center/trainings/${training.id}/edit`)}
                        className="p-2 border border-brand-secondary text-brand-secondary rounded-lg hover:bg-brand-secondary hover:text-white transition-all duration-200"
                        title="Modifier"
                      >
                        <Edit className="h-3 w-3" />
                      </button>
                      <button 
                        onClick={() => navigate(`/training-center/trainings/${training.id}`)}
                        className="p-2 border border-brand-mint text-brand-mint rounded-lg hover:bg-brand-mint hover:text-white transition-all duration-200"
                        title="Voir les détails"
                      >
                        <Eye className="h-3 w-3" />
                      </button>
                      <button 
                        onClick={() => handleDeleteTraining(training.id)}
                        className="p-2 border border-red-300 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-all duration-200"
                        title="Supprimer"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
              </div>
            </motion.div>
          );
        })}
      </div>

        {/* No results */}
        {filteredTrainings.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="h-12 w-12 text-brand-primary" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {trainings.length === 0 ? 'Aucune formation trouvée' : 'Aucune formation correspondant aux critères'}
            </h3>
            <p className="text-gray-600 mb-8 text-lg">
              {trainings.length === 0 
                ? 'Vous n\'avez pas encore de formations. Commencez par en créer une !'
                : 'Essayez de modifier vos critères de recherche'
              }
            </p>
            {trainings.length === 0 && (
              <button 
                onClick={async () => {
                  try {
                    const { canCreate, message } = await trainingsService.canCreateTraining();
                    if (canCreate) {
                      navigate('/training-center/trainings/create');
                    } else {
                      const go = window.confirm(`${message || "Vous devez avoir un abonnement actif pour créer une nouvelle formation."}\n\nSouhaitez-vous consulter les plans d'abonnement ?`);
                      if (go) navigate('/training-center/subscriptions');
                    }
                  } catch (e) {
                    navigate('/training-center/trainings/create');
                  }
                }}
                className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white px-8 py-4 rounded-xl font-semibold hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Créer ma première formation
              </button>
            )}
          </motion.div>
        )}

        {/* Approval Notification - Component not available */}

        {/* Session Creation Modal */}
        {showSessionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Ajouter une Session</h3>
                <button
                  onClick={handleCloseSessionModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de la session *
                  </label>
                  <input
                    type="date"
                    value={sessionDate}
                    onChange={(e) => setSessionDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleCloseSessionModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={isCreatingSession}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleCreateSession}
                    disabled={isCreatingSession || !sessionDate}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-brand-secondary to-brand-secondary/90 text-white rounded-lg hover:from-brand-secondary/90 hover:to-brand-secondary/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isCreatingSession ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Création...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Créer Session
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingsPage; 